/**
 * Application shell: header, navigation, footer and the skip link.
 */

import type { ReactNode } from 'react';
import { Link, useRouter } from '../lib/router';
import { useSession } from '../lib/session';
import { useTheme } from '../lib/theme';

const PUBLIC_NAV = [
  { to: '/', label: 'Product' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
];

const APP_NAV = [
  { to: '/app', label: 'New diagnosis' },
  { to: '/app/diagnoses', label: 'History' },
  { to: '/app/specs', label: 'Specs' },
  { to: '/app/billing', label: 'Billing' },
  { to: '/app/account', label: 'Account' },
];

function isActive(current: string, target: string): boolean {
  if (target === '/') return current === '/';
  if (target === '/app') return current === '/app';
  return current === target || current.startsWith(`${target}/`);
}

export function Layout({ children }: { children: ReactNode }) {
  const { path, navigate } = useRouter();
  const { theme, toggle } = useTheme();
  const { status, user, signOut } = useSession();
  const signedIn = status === 'signed_in';
  const nav = signedIn ? APP_NAV : PUBLIC_NAV;

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="site-header">
        <div className="container site-header__inner">
          <Link to={signedIn ? '/app' : '/'} className="brand" aria-label="API Rescue Desk home">
            <svg className="brand__mark" viewBox="0 0 64 64" aria-hidden="true">
              <rect
                width="64"
                height="64"
                rx="14"
                fill="var(--bg-inset)"
                stroke="var(--border-strong)"
              />
              <path
                d="M14 40h8l6-16h8l6 16h8"
                fill="none"
                stroke="var(--accent)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="32" cy="18" r="4" fill="var(--accent)" />
            </svg>
            API Rescue Desk
          </Link>

          <nav className="nav" aria-label="Main">
            {nav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                aria-current={isActive(path, item.to) ? 'page' : undefined}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="site-header__spacer" />

          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="button--ghost button--small"
              onClick={toggle}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </button>
            {signedIn ? (
              <>
                <span className="small faint nowrap" title={user?.email ?? undefined}>
                  {user?.displayName ?? user?.email ?? 'Signed in'}
                </span>
                <button
                  type="button"
                  className="button--small"
                  onClick={async () => {
                    await signOut();
                    navigate('/');
                  }}
                >
                  Sign out
                </button>
              </>
            ) : (
              <Link className="button button--primary button--small" to="/signin">
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>

      <main id="main" className="container">
        {children}
      </main>

      <footer className="site-footer">
        <div className="container site-footer__inner">
          <span>
            API Rescue Desk - turn an API failure into a corrected request and a clear incident
            report.
          </span>
          <span className="site-header__spacer" />
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/pricing">Pricing</Link>
        </div>
      </footer>
    </div>
  );
}

export function PageHead({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-head row row--between">
      <div>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="row">{actions}</div> : null}
    </div>
  );
}
